import { useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Activity, AlertTriangle, CheckCircle2, Clock3, Search, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { buttonVariants } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { MonitoringHealth, MonitoringOverview } from './types';
import {
  MonitoringUiFilters,
  filterMonitoringDevices,
  filterMonitoringIncidents,
  getHealthBadgeClass,
  getIncidentStateBadgeClass,
} from './helpers';

const defaultFilters: MonitoringUiFilters = {
  search: '',
  deviceFamily: 'ALL',
  deviceHealth: 'ALL',
  ruleCode: 'ALL',
  incidentState: 'ALL',
  showOnlyIssues: true,
  showInactive: false,
};

function SummaryCard({
  title,
  value,
  description,
  icon,
}: {
  title: string;
  value: number;
  description: string;
  icon: JSX.Element;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardDescription>{title}</CardDescription>
          <CardTitle className="mt-2 text-3xl">{value}</CardTitle>
        </div>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground">{description}</div>
      </CardContent>
    </Card>
  );
}

function formatTimestamp(value: Date | null) {
  if (!value) {
    return 'Ingen data';
  }

  return `${format(value, 'dd/MM/yyyy HH:mm')} (${formatDistanceToNow(value, {
    addSuffix: true,
  })})`;
}

function getHealthLabel(health: MonitoringHealth) {
  switch (health) {
    case 'critical':
      return 'Kritisk';
    case 'warning':
      return 'Advarsel';
    case 'inactive':
      return 'Inaktiv';
    default:
      return 'OK';
  }
}

export default function MonitoringPageContent({ overview }: { overview: MonitoringOverview }) {
  const [filters, setFilters] = useState<MonitoringUiFilters>(defaultFilters);

  const filteredDevices = useMemo(
    () => filterMonitoringDevices(overview.devices, filters),
    [overview.devices, filters]
  );
  const filteredIncidents = useMemo(
    () => filterMonitoringIncidents(overview.incidents, filters),
    [overview.incidents, filters]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Ældre overvågning</h1>
          <Link className="inline-flex items-center gap-2 py-2 font-medium text-sky-700" to="/monitoring/anomalies"><Activity size={16} /> Afvigelser og annotationer →</Link>
          <p className="text-sm text-muted-foreground">
            Senest evalueret{' '}
            {overview.summary.lastEvaluatedAt
              ? formatDistanceToNow(overview.summary.lastEvaluatedAt, { addSuffix: true })
              : 'ukendt'}
          </p>
        </div>
        <div className="flex justify-end lg:hidden">
          <Link className={buttonVariants({ variant: 'outline' })} to="/monitoring/rules">
            Regler
          </Link>
        </div>
        <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="relative sm:col-span-2 xl:col-span-1">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Search monitoring"
              className="pl-9"
              placeholder="Søg på organisation, sensor eller enhed"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
            />
          </div>

          <Select
            value={filters.deviceFamily}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                deviceFamily: value as MonitoringUiFilters['deviceFamily'],
              }))
            }
          >
            <SelectTrigger aria-label="Device family filter">
              <SelectValue placeholder="Familie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Alle familier</SelectItem>
              {overview.filters.deviceFamilies.map((family) => (
                <SelectItem key={family} value={family}>
                  {family}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.deviceHealth}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                deviceHealth: value as MonitoringUiFilters['deviceHealth'],
              }))
            }
          >
            <SelectTrigger aria-label="Device health filter">
              <SelectValue placeholder="Helbred" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Alle statusser</SelectItem>
              <SelectItem value="critical">Kritisk</SelectItem>
              <SelectItem value="warning">Advarsel</SelectItem>
              <SelectItem value="healthy">OK</SelectItem>
              <SelectItem value="inactive">Inaktiv</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.ruleCode}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                ruleCode: value as MonitoringUiFilters['ruleCode'],
              }))
            }
          >
            <SelectTrigger aria-label="Rule code filter">
              <SelectValue placeholder="Regel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Alle regler</SelectItem>
              {overview.filters.ruleCodes.map((ruleCode) => (
                <SelectItem key={ruleCode} value={ruleCode}>
                  {ruleCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.incidentState}
            onValueChange={(value) =>
              setFilters((current) => ({
                ...current,
                incidentState: value as MonitoringUiFilters['incidentState'],
              }))
            }
          >
            <SelectTrigger aria-label="Incident state filter">
              <SelectValue placeholder="Hændelsesstatus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Alle hændelser</SelectItem>
              {overview.filters.incidentStates.map((state) => (
                <SelectItem key={state} value={state}>
                  {state === 'active' ? 'Aktive' : 'Løste'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden lg:flex lg:justify-end">
          <Link className={buttonVariants({ variant: 'outline' })} to="/monitoring/rules">
            Regler
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="issues-only"
            checked={filters.showOnlyIssues}
            onCheckedChange={(checked) =>
              setFilters((current) => ({ ...current, showOnlyIssues: checked === true }))
            }
          />
          <Label htmlFor="issues-only">Vis kun enheder med problemer</Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="show-inactive"
            checked={filters.showInactive}
            onCheckedChange={(checked) =>
              setFilters((current) => ({ ...current, showInactive: checked === true }))
            }
          />
          <Label htmlFor="show-inactive">Vis inaktive enheder</Label>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        <SummaryCard
          title="Kritiske enheder"
          value={overview.summary.criticalDevices}
          description="Aktive enheder med kritisk status"
          icon={<ShieldAlert className="h-5 w-5 text-red-600" />}
        />
        <SummaryCard
          title="Advarsler"
          value={overview.summary.warningDevices}
          description="Aktive enheder med advarsel"
          icon={<AlertTriangle className="h-5 w-5 text-amber-600" />}
        />
        <SummaryCard
          title="OK"
          value={overview.summary.healthyDevices}
          description="Aktive enheder uden åbne hændelser"
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
        />
        <SummaryCard
          title="Inaktive"
          value={overview.summary.inactiveDevices}
          description="Enheder uden aktiv overvågning"
          icon={<Clock3 className="h-5 w-5 text-slate-500" />}
        />
        <SummaryCard
          title="Aktive hændelser"
          value={overview.summary.activeIncidents}
          description="Åbne overvågningshændelser"
          icon={<Activity className="h-5 w-5 text-red-600" />}
        />
        <SummaryCard
          title="Løste hændelser"
          value={overview.summary.resolvedIncidents}
          description="Historik i feedet"
          icon={<CheckCircle2 className="h-5 w-5 text-slate-600" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
        <Card>
          <CardHeader>
            <CardTitle>Enheder</CardTitle>
            <CardDescription>Prioriteret efter kritiske og advarsler først</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredDevices.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                Ingen enheder matcher filtrene.
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Enhed</TableHead>
                      <TableHead>Familie</TableHead>
                      <TableHead>Sidst data</TableHead>
                      <TableHead>Regler</TableHead>
                      <TableHead className="text-right">Link</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDevices.map((device) => (
                      <TableRow key={device.sensorId}>
                        <TableCell>
                          <Badge className={getHealthBadgeClass(device.currentHealth)}>
                            {getHealthLabel(device.currentHealth)}
                          </Badge>
                        </TableCell>
                        <TableCell>{device.orgTitle}</TableCell>
                        <TableCell>
                          <div className="font-medium">{device.sensorTitle}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>{device.deviceId}</span>
                            {device.hasRuleOverride && (
                              <Badge className="border-amber-200 bg-amber-100 text-amber-900">
                                Override
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{device.deviceFamily}</TableCell>
                        <TableCell>{formatTimestamp(device.lastDataAt)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {device.activeRuleCodes.length > 0 ? (
                              device.activeRuleCodes.map((ruleCode) => (
                                <Badge key={ruleCode} variant="outline">
                                  {ruleCode}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">Ingen</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-3">
                            <Link
                              className="text-sm font-medium text-sky-700"
                              to={device.detailPath}
                            >
                              Åbn
                            </Link>
                            <Link
                              className="text-sm font-medium text-sky-700"
                              to={`/monitoring/rules/devices/${device.sensorId}`}
                            >
                              Regler
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hændelsesfeed</CardTitle>
            <CardDescription>Seneste aktive og løste hændelser</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredIncidents.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                Ingen hændelser matcher filtrene.
              </div>
            ) : (
              filteredIncidents.map((incident) => (
                <div
                  key={incident.fingerprint}
                  className="rounded-md border p-4"
                  data-testid={`incident-${incident.fingerprint}`}
                >
                  {(() => {
                    const displaySummary =
                      incident.status === 'resolved' && incident.resolutionSummary
                        ? incident.resolutionSummary
                        : incident.summary;
                    const displayDetails =
                      incident.status === 'resolved' && incident.resolutionDetails
                        ? incident.resolutionDetails
                        : incident.details;
                    const showOriginalContext =
                      incident.status === 'resolved' &&
                      (incident.resolutionSummary || incident.resolutionDetails);

                    return (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="space-y-1">
                            <div className="font-medium">{displaySummary}</div>
                            <div className="text-sm text-muted-foreground">
                              {incident.orgTitle} · {incident.sensorTitle}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge className={getHealthBadgeClass(incident.severity)}>
                              {incident.severity}
                            </Badge>
                            <Badge className={getIncidentStateBadgeClass(incident.status)}>
                              {incident.status === 'active' ? 'Aktiv' : 'Løst'}
                            </Badge>
                          </div>
                        </div>
                        <div className="mt-3 text-sm text-muted-foreground">{displayDetails}</div>
                        {showOriginalContext && (
                          <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs text-muted-foreground">
                            <div className="font-medium text-slate-700">Oprindelig hændelse</div>
                            <div className="mt-1">{incident.summary}</div>
                            <div className="mt-1">{incident.details}</div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>{incident.ruleCode}</span>
                    <span>{formatTimestamp(incident.updatedAt)}</span>
                  </div>
                  <div className="mt-3">
                    <Link className="text-sm font-medium text-sky-700" to={incident.detailPath}>
                      Gå til sensor
                    </Link>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
